// eslint-disable-next-line no-restricted-imports -- This file defines useClientTerminology wrapper
import { type MessageDescriptor, useIntl } from 'react-intl';
import { type ClientContext, useUserClientContext } from '@/app/clientContext';
import { capitalize } from '@/helpers/stringConversion';
import { isPge } from '@/features/utils';
import { type PrimitiveType } from 'intl-messageformat';
import { type MessageId } from '@/assets/lang/en-US';
import type { TagField } from '@/features/types';

export const CLEAR = 'Clear';

/** The only tag; its id is what the backend filters on. */
export const CLEAR_TAG: TagField = { id: 1, name: CLEAR };

const replaceWithCase = (
  str: string,
  pattern: string,
  replacement: string,
  uppercase: boolean = false
) => {
  const matches = str.match(new RegExp(pattern, 'ig'));

  if (!matches || matches.length < 1) {
    return str;
  }
  const match = matches[0];

  if (uppercase) {
    return str.replace(match, replacement.toUpperCase());
  }

  if (match === pattern.toLowerCase()) {
    return str.replace(match, replacement.toLowerCase());
  }

  if (match === pattern.toUpperCase()) {
    return str.replace(match, replacement.toUpperCase());
  }

  return str.replace(match, capitalize(replacement));
};

const defaultTerminology = {
  level1Distribution: 'level1Distribution',
  level2Distribution: 'level2DistributionCircuit',
  level1Transmission: 'level1Transmission',
  level2Transmission: 'level2Transmission',
  level2DistributionHazardTrees: 'level2DistributionCircuitHazardTrees',
  level2DistributionTrees: 'level2DistributionCircuitTrees',
  level2TransmissionTrees: 'level2TransmissionTrees',
  level2TransmissionHazardTrees: 'level2TransmissionHazardTrees',
} as const;

const multiTerminology = {
  ...defaultTerminology,
  level1Distribution: 'level1DistributionDivision',
  level2Transmission: 'level2TransmissionSegment',
} as const;

const clientTranslate = (
  context: ClientContext,
  translation: string
): string => {
  // @team-iou PG&E-specific terminology
  // TODO: replace this with a call-out to json data or something
  if (isPge(context)) {
    if (translation.toLowerCase().includes('hazard tree')) {
      return replaceWithCase(translation, 'hazard', 'declining strike');
    } else if (translation.toLowerCase().includes('encroachment risk')) {
      return replaceWithCase(translation, 'risk', 'assessment');
    } else {
      return translation;
    }
  } else {
    return translation;
  }
};

export const useClientTerminology = () => {
  const { context } = useUserClientContext();
  const intl = useIntl();
  const clientTerminology =
    context.clientType === 'multi' ? multiTerminology : defaultTerminology;
  const unit = context.analysis_config.metric === 'ft' ? 'mile' : 'kilometer';

  const getClientTerm = (
    descriptor: MessageId,
    labelType?: keyof typeof clientTerminology,
    num?: number
  ) => {
    const translatedDescriptor =
      labelType && labelType in clientTerminology
        ? descriptor.replace(labelType, clientTerminology[labelType])
        : descriptor;

    if (num !== undefined) {
      return intl.formatMessage({ id: translatedDescriptor }, { num });
    }
    const translation = intl.formatMessage(
      { id: translatedDescriptor },
      { unit }
    );

    return clientTranslate(context, translation);
  };

  /**
   * Wrapper for intl.formatMessage that supports both plain strings (with client-specific terminology)
   * and rich text interpolation (for React elements).
   * Returns either a string or a ReactNode, depending on the values passed.
   */
  const formatMessage = (
    descriptor: { id: MessageId } & Omit<MessageDescriptor, 'id'>,
    values?: Record<
      string,
      PrimitiveType | ((chunks: string) => React.ReactNode)
    >
  ) => {
    const translation = intl.formatMessage(descriptor, { ...values, unit });

    // If it's a string, apply clientTranslate; if not, return as-is (supports React nodes for rich text)
    if (typeof translation === 'string') {
      return clientTranslate(context, translation);
    }
    return translation;
  };

  return {
    getClientTerm, // NOTE: Old usage
    formatMessage,
    formatNumber: intl.formatNumber, // Just pass this through for now
    formatNumberToParts: intl.formatNumberToParts, // Just pass this through for now
    formatDate: intl.formatDate,
    formatTime: intl.formatTime,
  };
};

export type ClientTerminology = ReturnType<typeof useClientTerminology>;
